/**
 * AutoLISP engine facade — bootstrap, script load/unload lifecycle and the
 * c: command runner. App entry (App.tsx) calls startLispRuntime() once at
 * boot; the dispatcher reaches c: commands exclusively through registry.ts.
 */

import { LispRuntime } from './interpreter';
import { registerAppApi } from './cadApi';
import { mkStr, printValue, type LispValue } from './values';
import {
    clearLispCommands,
    listLispCommands,
    resolveLispCommand,
    setLispCommandRunner,
} from './registry';
import { useDynamicInputStore } from '../../state/useDynamicInputStore';
import { useLispScriptsStore } from '../../state/useLispScriptsStore';

export interface LispLoadResult {
    ok: boolean;
    message: string;
    /** c: command names registered by this load. */
    commands: string[];
}

let engine: LispRuntime | null = null;

/** Lazily built singleton runtime with the app bridge installed. */
export function getLispEngine(): LispRuntime {
    if (!engine) {
        engine = new LispRuntime();
        // Transcript sink: princ/prompt/print land in the shared command log
        // (bottom command line + F2 text window).
        engine.out = (text) => useDynamicInputStore.getState().appendLog([text]);
        registerAppApi(engine);
    }
    return engine;
}

/** AutoLISP *error* handling + honest `; error:` transcript line. */
async function reportLispError(rt: LispRuntime, error: unknown): Promise<string> {
    const message = error instanceof Error ? error.message : String(error);
    const handler = rt.globalEnv.lookup('*ERROR*');
    if (handler.k === 'usubr') {
        try {
            await rt.apply(handler, [mkStr(message)]);
        } catch {
            // A failing *error* handler is not fatal — the message still prints.
        }
    }
    rt.out(`; error: ${message}`);
    return message;
}

/** Load one LISP source string; registers its c: commands. */
export async function loadLispSource(src: string, sourceName: string): Promise<LispLoadResult> {
    const rt = getLispEngine();
    const before = new Set(listLispCommands());
    rt.commandSource = sourceName;
    try {
        await rt.evalString(src);
        const added = listLispCommands().filter((n) => !before.has(n));
        return { ok: true, message: `Loaded ${sourceName}.`, commands: added };
    } catch (error) {
        const message = await reportLispError(rt, error);
        return { ok: false, message: `; error: ${message}`, commands: [] };
    } finally {
        rt.commandSource = null;
    }
}

/** Drop the c: commands contributed by one source script. */
export function unloadLispSource(sourceName: string): number {
    return clearLispCommands(sourceName);
}

/** Fire-and-forget c: command execution (dispatcher bridge target). */
async function runLispCommand(name: string): Promise<void> {
    const rt = getLispEngine();
    const entry = resolveLispCommand(name);
    if (!entry || entry.fn.k !== 'usubr') return;
    useDynamicInputStore.getState().appendLog([`Command: ${name}`]);
    rt.clearCancel();
    try {
        const result: LispValue = await rt.apply(entry.fn, []);
        rt.out(printValue(result, true));
    } catch (error) {
        await reportLispError(rt, error);
    }
}

let started = false;

/**
 * Boot the engine once: build the runtime, install the dispatcher bridge,
 * arm Escape-cancellation and load the startup suite (persisted scripts).
 */
export function startLispRuntime(): void {
    if (started) return;
    started = true;
    getLispEngine();
    setLispCommandRunner((name) => {
        void runLispCommand(name);
    });
    window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') getLispEngine().cancel();
    });
    void loadStartupScripts();
}

/** Sequentially load the persisted startup suite; one failure never stops the rest. */
async function loadStartupScripts(): Promise<void> {
    const store = useLispScriptsStore.getState();
    for (const script of store.scripts.filter((s) => s.loadAtStartup)) {
        const result = await loadLispSource(script.content, script.id);
        store.setStatus(script.id, result.ok ? 'loaded' : 'error', result.message, result.commands);
    }
}
